/** Safely parses a JSON string, returning `fallback` instead of throwing on invalid input. */
export function safeJsonParse<T = any>(str: string, fallback: T | null = null): T | null {
  try {
    return JSON.parse(str);
  } catch (err) {
    return fallback;
  }
}

/** Safely stringifies a value, returning `fallback` instead of throwing (e.g. circular refs). Accepts the same optional `replacer` and `space` as JSON.stringify (e.g. `Object.getOwnPropertyNames(err)` to serialize Error objects). */
export function safeJsonStringify(
  value: unknown,
  fallback: string | null = null,
  replacer?: (number | string)[] | ((key: string, value: unknown) => unknown) | null,
  space?: string | number
): string | null {
  try {
    return JSON.stringify(value, replacer as any, space);
  } catch (err) {
    return fallback;
  }
}

/** Deep-copies a value through a JSON round trip (same semantics as `JSON.parse(JSON.stringify(value))`), returning `fallback` (default: the original value) instead of throwing (e.g. circular refs). Like JSON.parse it returns `any` unless `T` is given: the copy is plain data (e.g. a Sequelize instance becomes its JSON object), not a `T`. */
export function safeJsonClone<T = any>(value: unknown, fallback: T = value as T): T {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (err) {
    return fallback;
  }
}
